import { useRef, useState, useCallback, useEffect } from "react";
import Webcam from "react-webcam";
import ColorThief from "colorthief";
import { CustomModal } from "../../../common";
import { WebcamContainer, CaptureButton, PreviewImage, ButtonContainer, RetakeButton } from "./webcam-style";

interface WebcamCaptureProps {
    isOpen: boolean;
    onClose: () => void;
    onCapture: (colorHex: string) => void;
}

export const WebcamCapture: React.FC<WebcamCaptureProps> = ({ isOpen, onClose, onCapture }) => {
    const webcamRef = useRef<Webcam>(null);
    const [capturedImage, setCapturedImage] = useState<string | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [hasPermission, setHasPermission] = useState<boolean | null>(null);

    const rgbToHex = (r: number, g: number, b: number): string => {
        return "#" + [r, g, b].map(x => {
            const hex = x.toString(16);
            return hex.length === 1 ? "0" + hex : hex;
        }).join("");
    };

    const processImage = useCallback((imageSrc: string) => {
        // Criar uma imagem para processar com ColorThief
        const img = new Image();
        img.src = imageSrc;

        img.onload = () => {
            try {
                const colorThief = new ColorThief();
                const dominantColor = colorThief.getColor(img);
                
                const hex = rgbToHex(dominantColor[0], dominantColor[1], dominantColor[2]);

                setIsProcessing(false);
                onCapture(hex);                
                onClose();
                setCapturedImage(null);
            } catch (error) {
                console.error("Erro ao extrair cor:", error);
                setIsProcessing(false);
            }
        };

        img.onerror = () => {
            console.error("Erro ao carregar imagem");
            setIsProcessing(false);
        };
    }, [onCapture, onClose]);

    const capture = useCallback(() => {
        if (capturedImage) {
            setIsProcessing(true);
            processImage(capturedImage);
        } else {
            const imageSrc = webcamRef?.current?.getScreenshot() ?? null;
            console.log(`imageSrc ${imageSrc}`);
            setCapturedImage(imageSrc);
        }
    }, [capturedImage, processImage]);

    const retake = () => {
        setCapturedImage(null);
    };

    const handleClose = () => {
        setCapturedImage(null);
        setIsProcessing(false);
        setError(null);
        setHasPermission(null);
        onClose();
    };

    // Verificar suporte e permissões da câmera
    useEffect(() => {
        if (!isOpen) return;

        const checkCameraSupport = async () => {
            // Verificar se a API está disponível
            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                setError("Seu navegador não suporta acesso à câmera. Use um navegador moderno ou atualize o seu.");
                setHasPermission(false);
                return;
            }

            // Verificar permissões
            try {
                // Tentar acessar a câmera para verificar permissões
                const stream = await navigator.mediaDevices.getUserMedia({
                    video: { facingMode: "environment" }
                });
                // Se chegou aqui, tem permissão
                setHasPermission(true);
                setError(null);
                // Parar o stream imediatamente, o Webcam vai criar o seu próprio
                stream.getTracks().forEach(track => track.stop());
            } catch (err: any) {
                setHasPermission(false);
                if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
                    setError("Permissão de câmera negada. Por favor, permita o acesso à câmera nas configurações do navegador.");
                } else if (err.name === "NotFoundError" || err.name === "DevicesNotFoundError") {
                    setError("Nenhuma câmera encontrada no dispositivo.");
                } else if (err.name === "NotReadableError" || err.name === "TrackStartError") {
                    setError("A câmera está sendo usada por outro aplicativo. Feche outros apps que usam a câmera.");
                } else {
                    setError("Erro ao acessar a câmera. Verifique se está usando HTTPS ou localhost.");
                }
            }
        };

        checkCameraSupport();
    }, [isOpen]);

    return (
        <CustomModal
            isOpen={isOpen}
            onRequestClose={handleClose}
            contentLabel="Capturar cor com câmera"
            hideHeader={true}
        >
            <WebcamContainer>
                {!capturedImage ? (
                    <>
                        {hasPermission && !error ? (
                            <Webcam
                                audio={false}
                                ref={webcamRef}
                                screenshotFormat="image/jpeg"
                                screenshotQuality={0.92}
                                videoConstraints={{
                                    facingMode: "environment",
                                    // width: { ideal: 1280 },
                                    height: { ideal: 300 },
                                    // aspectRatio: { ideal: 16/9 }
                                }}
                                onUserMediaError={(error) => {
                                    console.error("Erro ao acessar câmera:", error);
                                    setError("Erro ao iniciar a câmera. Tente novamente.");
                                    setHasPermission(false);
                                }}
                            />
                        ) : error ? (
                            <div style={{
                                padding: "20px",
                                textAlign: "center",
                                color: "#ff4444",
                                backgroundColor: "#ffe6e6",
                                borderRadius: "8px",
                                margin: "20px 0"
                            }}>
                                <p style={{ margin: "10px 0" }}>{error}</p>
                                <p style={{ fontSize: "14px", marginTop: "10px", color: "#666" }}>
                                    Dica: Em dispositivos móveis, certifique-se de estar usando HTTPS.
                                </p>
                            </div>
                        ) : (
                            <div style={{
                                padding: "20px",
                                textAlign: "center"
                            }}>
                                Verificando permissões da câmera...
                            </div>
                        )}
                        {

                            <ButtonContainer>
                                <CaptureButton
                                    onClick={capture}
                                    disabled={isProcessing || !hasPermission || !!error}
                                >
                                    {isProcessing ? "Processando..." : "Capturar Foto"}
                                </CaptureButton>
                            </ButtonContainer>
                        }
                    </>
                ) : (
                    <>
                        <PreviewImage src={capturedImage} alt="Preview" />
                        <ButtonContainer>
                            <CaptureButton onClick={capture} disabled={isProcessing}>
                                {isProcessing ? "Processando..." : "Usar esta foto"}
                            </CaptureButton>
                            <RetakeButton onClick={retake}>Tirar outra foto</RetakeButton>
                        </ButtonContainer>
                    </>
                )}
            </WebcamContainer>
        </CustomModal>
    );
};

